import React, { useEffect } from 'react'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from 'redux/config/store'
import Loading from 'core-system/Loading'
import GridCard from 'employee-platform/shared/components/GridCard'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import { Flex as FlexIcon } from 'core-system/Icons/Sidebar'
import palette from 'core-system/Themes/palette'
import Text from 'core-system/Text'
import FormattingUtils from 'shared/FormattingUtils'
import { formatCurrency } from 'shared/CurrencyFunctions'
import { CurrencyType } from 'features/Programs/Shared/types'
import moment from 'moment'
import styled from 'styled-components'
import FlexContainer from 'core-system/FlexContainer'
import Divider from 'core-system/Divider'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import * as FlexIcons from 'core-system/Icons/Flex'
import * as MicromobilityIcons from 'core-system/Icons/Micromobility'
import { Car as CarIcon } from 'core-system/Icons/modeTypes'

const Container = styled.div`
  padding: 2rem;
`

const ProgramsHeader = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const HeaderIconCircle = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: ${palette.secondary.purple100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ProgramSummaryRow = styled(FlexContainer)`
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
`

const ProgramTitleRow = styled(FlexContainer)`
  align-items: center;
  gap: 0.5rem;
`

const SmallPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background-color: ${palette.grey.grey100};
  color: ${palette.text.secondary};
  font-size: 0.75rem;
  line-height: 1.1;
`

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const EligibleCategoriesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.125rem;
`

const CategoryItem = styled(FlexContainer)`
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.25rem 0;
`

const CategoryIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background-color: ${palette.grey.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${palette.text.secondary};
`

const CategoryInfo = styled(FlexContainer)`
  flex-direction: column;
  gap: 0.0625rem;
  flex: 1;
  min-width: 0;
`

const EmptyState = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: ${palette.text.secondary};
`

const ResetRow = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
`

const ProgramInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const getDaysUntilReset = (cycleEnd: string): number => {
  const days = moment(cycleEnd).diff(moment(), 'days')
  return Math.max(0, days)
}

const getCycleType = (
  cycleStart: string,
  cycleEnd: string
): 'daily' | 'monthly' => {
  const daysDiff = moment(cycleEnd).diff(moment(cycleStart), 'days')
  // If cycle is approximately 1 day (or less), it's daily, otherwise monthly
  return daysDiff <= 1 ? 'daily' : 'monthly'
}

const getProgramDisplayName = (program: BaseProgram) => {
  const superName = (program.superProgramName ?? '').trim()
  const name = (program.name ?? '').trim()
  return superName || name || 'Flex'
}

const formatSegmentPillLabel = (segmentName: string) => {
  // Match employer-side worksite formatting: "Worksite 475 Sansome St, SF, CA" -> "475 Sansome St"
  if (!segmentName) return segmentName
  if (!segmentName.includes('Worksite')) return segmentName

  const formatted =
    segmentName.split(',')[0]?.replace('Worksite ', '').trim() || segmentName
  return formatted
}

type ProgramOptionKey =
  | 'gas'
  | 'parking'
  | 'transit'
  | 'shared'
  | 'leasing'
  | 'yearlyPool'

type ProgramOption = {
  key: ProgramOptionKey
  label: string
  description: string
  icon: React.ReactNode
}

const programOptions: Record<ProgramOptionKey, ProgramOption> = {
  gas: {
    key: 'gas',
    label: 'Gas',
    description: 'Use your monthly budget to purchase gas at any gas station.',
    icon: (
      <FlexIcons.Gas width={16} height={16} color={palette.text.secondary} />
    ),
  },
  parking: {
    key: 'parking',
    label: 'Parking',
    description: 'Use your monthly budget to pay for parking.',
    icon: (
      <FlexIcons.Parking
        width={16}
        height={16}
        color={palette.text.secondary}
      />
    ),
  },
  transit: {
    key: 'transit',
    label: 'Transit',
    description:
      'Use your monthly budget to pay for public transportation, subway, train, bus, and transit passes.',
    icon: (
      <FlexIcons.Transit
        width={16}
        height={16}
        color={palette.text.secondary}
      />
    ),
  },
  shared: {
    key: 'shared',
    label: 'Shared',
    description: 'Rent micromobility vehicles on a per-trip basis.',
    icon: (
      <MicromobilityIcons.SharedMicromobility
        width={16}
        height={16}
        color={palette.text.secondary}
      />
    ),
  },
  leasing: {
    key: 'leasing',
    label: 'Leasing',
    description:
      'Get a monthly membership to lease a micromobility vehicle of your choice.',
    icon: (
      <MicromobilityIcons.Leasing
        width={16}
        height={16}
        color={palette.text.secondary}
      />
    ),
  },
  yearlyPool: {
    key: 'yearlyPool',
    label: 'One-time purchase',
    description: 'One-time purchase option (uses a yearly pool).',
    icon: (
      <MicromobilityIcons.OneTimePurchase
        width={16}
        height={16}
        color={palette.text.secondary}
      />
    ),
  },
}

const programOptionKeys: ProgramOptionKey[] = [
  'gas',
  'parking',
  'transit',
  'shared',
  'leasing',
  'yearlyPool',
]

const getEligibleCategories = (program: BaseProgram): ProgramOption[] => {
  const p: any = program
  const available = programOptionKeys.filter((k) => Boolean(p[k]))
  if (available.length === 0) {
    return [
      {
        key: 'transit',
        label: 'Transportation spend',
        description:
          'Eligible on ground transportation spend for your daily commute.',
        icon: <CarIcon width={18} height={18} color={palette.text.secondary} />,
      },
    ]
  }
  return available.map((k) => programOptions[k])
}

const EmployeeFlexOverviewView = React.memo(() => {
  const dispatch = useDispatch()
  const { programs, profileData, wallet } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const conversionRates = useSelector(
    (state: AppState) => state.application.properties
  )
  const { allPrograms } = programs

  // Get currency from profileData, default to USD if not available
  const currency: CurrencyType =
    (profileData?.worksiteCurrency as CurrencyType) || 'USD'

  const flexPrograms: BaseProgram[] = allPrograms?.FLEX
    ? Object.values(allPrograms.FLEX).filter((p) => p.status === 'ACTIVE')
    : []

  const segmentsById: Record<string, string> = profileData?.segments
    ? profileData.segments.reduce(
        (acc, s) => ({ ...acc, [s.id]: s.name }),
        {} as Record<string, string>
      )
    : {}

  // Get charges/transactions - filter by program if possible
  // For now, we'll show all charges. In the future, charges could be tagged with programId
  const charges = wallet?.allCharges || []
  const flexCharges = charges.filter((charge) => {
    // Filter charges that might be related to Flex program
    // This is a simplified filter - in production, charges should have programId
    return charge.type !== 'pretax' && charge.status === 'succeeded'
  })

  useEffect(() => {
    // Fetch programs if not already loaded
    if (allPrograms === null) {
      dispatch(employeePlatformActions.getAllPrograms())
    }
  }, [dispatch, allPrograms])

  useEffect(() => {
    // Fetch charges if not already loaded
    if (!wallet?.allCharges) {
      dispatch(employeePlatformActions.getAllCharges())
    }
  }, [dispatch, wallet?.allCharges])

  // Show loading only if programs haven't been loaded yet
  // If programs are loaded but no Flex program exists, show empty state
  // Note: allPrograms can be null (not loaded) or {} (loaded but empty)
  if (allPrograms === null) {
    return <Loading fullPage />
  }

  if (flexPrograms.length === 0) {
    return (
      <Container>
        <GridCard headerTitle='Flex Programs'>
          <EmptyState>
            <Text variant='h4' textColor={palette.text.secondary}>
              No Active Flex Program
            </Text>
            <Text variant='action3' textColor={palette.text.secondary}>
              You don't have access to a Flex program at this time.
            </Text>
          </EmptyState>
        </GridCard>
      </Container>
    )
  }

  return (
    <Container>
      <ProgramsHeader>
        <FlexContainer alignItems='center' style={{ gap: '0.75rem' }}>
          <HeaderIconCircle>
            <FlexIcon
              width={18}
              height={18}
              color={palette.secondary.purple600}
            />
          </HeaderIconCircle>
          <Text variant='h2'>Flex Programs</Text>
        </FlexContainer>
        <Text variant='action3' textColor={palette.text.secondary}>
          {flexPrograms.length} program{flexPrograms.length === 1 ? '' : 's'}
        </Text>
      </ProgramsHeader>

      <GridContainer numColumns={2} customGridGap='1.5rem'>
        {flexPrograms.map((program) => {
          // Hardcoded for demo: $42 remaining of $60 total (values in cents)
          const remaining = 4200
          const total = 6000
          const daysUntilReset = getDaysUntilReset(program.cycleEnd)
          const cycleType = getCycleType(program.cycleStart, program.cycleEnd)
          const rawSegmentName = segmentsById[program.segment] ?? null
          const segmentName = rawSegmentName
            ? formatSegmentPillLabel(rawSegmentName)
            : null
          const categories = getEligibleCategories(program)

          return (
            <GridCard
              key={program.id}
              headerTitle={getProgramDisplayName(program)}
              borderColor={palette.secondary.purple400}
            >
              <div>
                <FlexContainer
                  flexDirection='column'
                  style={{ gap: '0.75rem' }}
                >
                  {/* Program info (top) */}
                  <ProgramInfoSection>
                    <div data-flex-card-section='summary'>
                      <ProgramSummaryRow>
                        <FlexContainer
                          alignItems='baseline'
                          style={{
                            gap: '0.375rem',
                            rowGap: '0.25rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Text
                            variant='h2'
                            textColor={palette.secondary.purple600}
                          >
                            {formatCurrency(
                              currency,
                              remaining,
                              conversionRates
                            )}
                          </Text>
                          <Text
                            variant='action2'
                            textColor={palette.text.secondary}
                          >
                            / {formatCurrency(currency, total, conversionRates)}
                          </Text>
                          <SmallPill>
                            {cycleType === 'daily'
                              ? 'Daily budget'
                              : 'Monthly budget'}
                          </SmallPill>
                        </FlexContainer>
                        {segmentName ? (
                          <SmallPill>{segmentName}</SmallPill>
                        ) : null}
                      </ProgramSummaryRow>
                    </div>

                    <ResetRow data-flex-card-section='reset'>
                      <Text
                        variant='action4'
                        textColor={palette.text.secondary}
                      >
                        Resets in {daysUntilReset}{' '}
                        {daysUntilReset === 1 ? 'day' : 'days'}
                      </Text>
                      <Text
                        variant='action4'
                        textColor={palette.text.secondary}
                      >
                        {moment(program.cycleEnd).format('MMM D, YYYY')}
                      </Text>
                    </ResetRow>
                  </ProgramInfoSection>

                  {/* Divider between program info and eligible categories (matches mobile hierarchy) */}
                  <Divider margin='0.5rem 0' />

                  {/* Eligible categories (below divider) */}
                  <EligibleCategoriesSection
                    aria-label='Eligible categories'
                    data-flex-card-section='categories'
                  >
                    <Text variant='action4' textColor={palette.text.secondary}>
                      Eligible categories
                    </Text>
                    {categories.map((cat, idx) => (
                      <React.Fragment key={`${cat.key}-${idx}`}>
                        {idx > 0 ? <Divider margin='0.25rem 0' /> : null}
                        <CategoryItem data-flex-category-item>
                          <CategoryIcon>{cat.icon}</CategoryIcon>
                          <CategoryInfo>
                            <Text variant='action2' fontWeight={600}>
                              {cat.label}
                            </Text>
                            <Text
                              variant='action4'
                              textColor={palette.text.secondary}
                            >
                              {cat.description}
                            </Text>
                          </CategoryInfo>
                        </CategoryItem>
                      </React.Fragment>
                    ))}
                  </EligibleCategoriesSection>
                </FlexContainer>
              </div>
            </GridCard>
          )
        })}
      </GridContainer>

      <Divider marginY='1.5rem' />

      <GridCard
        headerTitle='Recent Activity'
        borderColor={palette.secondary.purple400}
      >
        {flexCharges.length === 0 ? (
          <EmptyState>
            <Text variant='action3' textColor={palette.text.secondary}>
              No Transactions Recorded
            </Text>
          </EmptyState>
        ) : (
          <FlexContainer flexDirection='column' style={{ gap: '0.75rem' }}>
            {flexCharges.slice(0, 6).map((charge, idx) => (
              <React.Fragment key={charge.internalId}>
                {idx > 0 ? <Divider /> : null}
                <FlexContainer
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <FlexContainer
                    flexDirection='column'
                    style={{ gap: '0.25rem' }}
                  >
                    <Text variant='action2'>{charge.name}</Text>
                    <Text variant='action4' textColor={palette.text.secondary}>
                      {moment(charge.created).format('MMM D, YYYY')}
                    </Text>
                  </FlexContainer>
                  <Text variant='action2' fontWeight={600}>
                    {formatCurrency(currency, charge.amount, conversionRates)}
                  </Text>
                </FlexContainer>
              </React.Fragment>
            ))}
          </FlexContainer>
        )}
      </GridCard>
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  EmployeeFlexOverviewView.displayName = 'EmployeeFlexOverviewView'
}

export default EmployeeFlexOverviewView
